import { z } from 'zod';
import { idSchema, patchOf } from './common';

const optionalText = (max: number) => z.string().trim().max(max).nullable().default(null);
const localizedNames = z.record(z.string(), z.string().trim().max(120));

export const createLocationSchema = z.object({
  name: z.string().trim().min(1).max(120),
  nameLocalized: localizedNames.default({}).describe('The name per locale, such as { ka: "ვაკე" }; a locale missing here shows name'),
  address: optionalText(240),
  city: optionalText(120),
  phone: optionalText(40),
  sortOrder: z.number().int().default(0),
});
export type CreateLocationInput = z.infer<typeof createLocationSchema>;

export const updateLocationSchema = patchOf(createLocationSchema).extend({
  isPrimary: z
    .literal(true)
    .optional()
    .describe('Makes this branch the primary one; the previous primary stops being it'),
  archived: z
    .boolean()
    .optional()
    .describe('Archives or restores the branch; the primary branch cannot be archived'),
});
export type UpdateLocationInput = z.infer<typeof updateLocationSchema>;

export const createRoomSchema = z.object({
  name: z.string().trim().min(1).max(80),
  sortOrder: z.number().int().min(0).max(10_000).default(0),
});
export type CreateRoomInput = z.infer<typeof createRoomSchema>;

export const updateRoomSchema = patchOf(createRoomSchema);
export type UpdateRoomInput = z.infer<typeof updateRoomSchema>;

export const roomSchema = z.object({
  id: idSchema,
  locationId: idSchema,
  name: z.string(),
  sortOrder: z.number().int(),
});
export type RoomDto = z.infer<typeof roomSchema>;

export const locationSchema = z.object({
  id: idSchema,
  name: z.string(),
  nameLocalized: z.record(z.string(), z.string()),
  address: z.string().nullable(),
  city: z.string().nullable(),
  phone: z.string().nullable(),
  isPrimary: z.boolean(),
  sortOrder: z.number().int(),
  archivedAt: z.string().nullable(),
  readerCount: z.number().int(),
  rooms: z.array(roomSchema).describe('The rooms classes happen in, in their order'),
});
export type LocationDto = z.infer<typeof locationSchema>;

export const locationListQuerySchema = z.object({
  includeArchived: z.stringbool().default(false),
});
export type LocationListQuery = z.infer<typeof locationListQuerySchema>;
